import React from 'react';
import { useTranslation } from 'react-i18next';
import CopyIcon from '../../../../../images/copy-icon.png';
import '../SharedControlPanel.scss';
import './StreamSourcePanel.scss';
import AudioDeviceInput from '../../../../InputComponents/AudioDeviceInput/AudioDeviceInput';
import useProject from '../../../../../hooks/useProject';
import LanguageInput from '../../../../InputComponents/LanguageInput/LanguageInput';
import Help from '../../../../Help/Help';

type StreamSourcePanelProps = {
  sourceUrl: string | undefined;
  sourceKey: string | undefined;
};

const StreamSourcePanel = ({ sourceUrl, sourceKey }: StreamSourcePanelProps) => {
  const { t } = useTranslation();
  const { project } = useProject();

  const copyToClipboard = (copyValue: string) => {
    navigator.clipboard.writeText(copyValue);
  };

  const rtmpToSrtUrl = (url: string) => {
    return `${url?.replace('rtmps', 'srt').replace('rtmp', 'srt').replace('/live/', ':10000?streamid=live')}`;
  };

  const rtmpToConcetenatedSrtUrl = (url: string) => {
    return `${url?.replace('rtmps', 'srt').replace('rtmp', 'srt').replace('/live/', ':10000?streamid=live/')}${sourceKey}`;
  };


  return (
    <div className="stream-source-panel">
      <div className="stream-source-panel-section level">
        <div className="stream-source-panel-section-content">
          <p className="stream-source-panel-section-content-title">
            {t('producer.control-panel.stream-source.stream-key')}
            <Help title='Stream Key' text='Copy and paste this Stream Key to your software or encoder to send your stream to AccessLOOP.'></Help>
          </p>
          <p className="stream-source-panel-section-content-value">{sourceKey}</p>
        </div>
        {sourceKey ? (
          <div className="level-right">
            <button
              onClick={() => copyToClipboard(sourceKey)}
              className="stream-source-panel-copy-button"
            >
              {t('producer.control-panel.stream-source.copy-text')}{' '}
              <img src={CopyIcon} alt="clipboard copy" className="stream-source-panel-copy-image" />
            </button>
          </div>
        ) : null}
      </div>
      <hr className="control-panel-section-divider" />
      <div className="stream-source-panel-section level">
        <div className="stream-source-panel-section-content">
          <p className="stream-source-panel-section-content-title">
            {"Stream RTMPS URL"}
            <Help
              title="Stream RTMPS URL"
              text="Copy and paste this RTMPS Stream URL to your software or encoder to send your stream to AccessLOOP."
            />
          </p>
          <p className="stream-source-panel-section-content-value">{sourceUrl}</p>
        </div>
        {sourceUrl ? (
          <div className="level-right">
            <button
              onClick={() => copyToClipboard(sourceUrl)}
              className="stream-source-panel-copy-button"
            >
              {t('producer.control-panel.stream-source.copy-text')}{' '}
              <img src={CopyIcon} alt="clipboard copy" className="stream-source-panel-copy-image" />
            </button>
          </div>
        ) : null}
      </div>

      <hr className="control-panel-section-divider" />
      <div className="stream-source-panel-section level">
        <div className="stream-source-panel-section-content">
          <p className="stream-source-panel-section-content-title">
            {"Stream SRT URL"}
            <Help
              title="Stream SRT URL"
              text="Copy and paste this SRT Stream URL to your software or encoder to send your stream to AccessLOOP.<br/></br>Please note: depending on the encoder you are using you may need to use this standard SRT Url or the concatenated SRT Url. If you use this Url and you cannot get a stream to come through please try using the concatenated SRT Url instead."
            />
          </p>
          <p className="stream-source-panel-section-content-value">{rtmpToSrtUrl(sourceUrl ?? '')}</p>
        </div>
        {sourceUrl ? (
          <div className="level-right">
            <button
              onClick={() => copyToClipboard(rtmpToSrtUrl(sourceUrl ?? '') ?? '')}
              className="stream-source-panel-copy-button"
            >
              {t('producer.control-panel.stream-source.copy-text')}{' '}
              <img src={CopyIcon} alt="clipboard copy" className="stream-source-panel-copy-image" />
            </button>
          </div>
        ) : null}
      </div>

      <hr className="control-panel-section-divider" />
      <div className="stream-source-panel-section level">
        <div className="stream-source-panel-section-content">
          <p className="stream-source-panel-section-content-title">
            {"Stream SRT URL (concatenated)"}
            <Help
              title="Stream SRT URL (concatenated)"
              text="Copy and paste this concatenated SRT Stream URL to your software or encoder to send your stream to AccessLOOP.<br/></br>Please note: depending on the encoder you are using you may need to use this concatenated SRT Url or the standard SRT Url. If you use this concatenated Url and you cannot get a stream to come through please try using the standard SRT Url instead."
            />
          </p>
          <p className="stream-source-panel-section-content-value">{rtmpToConcetenatedSrtUrl(sourceUrl ?? '')}</p>
        </div>
        {sourceUrl ? (
          <div className="level-right">
            <button
              onClick={() => copyToClipboard(rtmpToConcetenatedSrtUrl(sourceUrl ?? '') ?? '')}
              className="stream-source-panel-copy-button"
            >
              {t('producer.control-panel.stream-source.copy-text')}{' '}
              <img src={CopyIcon} alt="clipboard copy" className="stream-source-panel-copy-image" />
            </button>
          </div>
        ) : null}
      </div>

      <hr className="control-panel-section-divider" />

      <div className="captioning-section captioning-settings-section">
        <div className="captioning-settings-position captioning-settings-position-block-with-title">
          <div className="captioning-settings-open-closed-captions-section">
            <div className="level level-left">
              <LanguageInput />
            </div>
          </div>
        </div>
      </div>

      {project?.createdByUser?.isAdmin === true && (
        <>
          <hr className="control-panel-section-divider" />
          <div className="stream-source-panel-section">
            <div className="stream-source-panel-content">
              <div className="level level-left">
                <AudioDeviceInput />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(StreamSourcePanel);
